"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import dynamic from "next/dynamic";
import { ChevronLeft, ChevronRight } from "lucide-react";
import MediaImage from "@/components/images/MediaImage";
import { clampImageIndex, normalizeCardMedia } from "@/lib/imageViewer.mjs";

const ImageViewer = dynamic(() => import("@/components/images/ImageViewer"), { ssr: false });
const arrowClass = "absolute top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[var(--card)] text-[var(--fg)] shadow-sm ring-1 ring-[var(--border)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] disabled:opacity-30";

/** Shared post/reply media. Carousels use a stable square frame capped by maxHeight. */
export default function CardMedia({ items = [], maxHeight = 520, variant = "single", rounded = true, className = "" }) {
  const slides = useMemo(() => normalizeCardMedia(items), [items]);
  const images = slides.filter((item) => item.type === "image");
  const collectionKey = JSON.stringify(slides.map(({ src, storagePath, type }) => [src, storagePath, type]));
  const scrollerRef = useRef(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(index);
  indexRef.current = index;
  const [viewerIndex, setViewerIndex] = useState(null);
  const isCarousel = variant === "carousel" || (variant === "single" && slides.length > 1);

  useEffect(() => {
    setIndex(0);
    setViewerIndex(null);
    scrollerRef.current?.scrollTo({ left: 0, behavior: "instant" });
  }, [collectionKey]);

  const scrollToIndex = useCallback((nextIndex) => {
    const el = scrollerRef.current;
    if (!el) return;
    const next = clampImageIndex(nextIndex, slides.length);
    el.scrollTo({ left: next * el.clientWidth, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    setIndex(next);
  }, [slides.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(() => el.scrollTo({ left: indexRef.current * el.clientWidth, behavior: "instant" }));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isCarousel, collectionKey]);

  if (!slides.length) return null;
  const frameClass = `relative mt-3 overflow-hidden ring-1 ring-[var(--border)] bg-[var(--surface-2)] ${rounded ? "rounded-[var(--radius)]" : ""} ${className}`;
  const media = (item, slideIndex) => item.type === "video" ? (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video controls className="block h-full w-full object-contain" style={{ maxHeight }}><source src={item.src} /></video>
  ) : (
    <button
      type="button"
      className={`block w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand)] ${isCarousel ? "h-full" : ""}`}
      style={{ maxHeight }}
      aria-label={`Expand image ${images.indexOf(item) + 1}${item.alt ? `: ${item.alt}` : ""}`}
      onClick={(event) => {
        event.stopPropagation();
        event.currentTarget.focus({ preventScroll: true });
        setViewerIndex(images.indexOf(item));
      }}
      tabIndex={isCarousel && slideIndex !== index ? -1 : 0}
    >
      <MediaImage item={item} className={`block w-full object-contain ${isCarousel ? "h-full" : ""}`} style={{ maxHeight }} />
    </button>
  );

  return (
    <>
      <div
        className={frameClass}
        style={isCarousel ? { aspectRatio: "1 / 1", maxHeight } : { maxHeight }}
        role={isCarousel ? "region" : undefined}
        aria-label={isCarousel ? "Post media carousel" : undefined}
        onKeyDown={(event) => {
          if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
          event.preventDefault();
          event.stopPropagation();
          scrollToIndex(index + (event.key === "ArrowRight" ? 1 : -1));
        }}
      >
        {isCarousel ? (
          <>
            <div ref={scrollerRef} className="no-scrollbar absolute inset-0 flex snap-x snap-mandatory overflow-x-auto" onScroll={(event) => {
              const el = event.currentTarget;
              if (el.clientWidth) setIndex(clampImageIndex(Math.round(el.scrollLeft / el.clientWidth), slides.length));
            }}>
              {slides.map((item, i) => <div key={`${item.src}|${item.storagePath}|${i}`} className="relative h-full w-full shrink-0 snap-center">{media(item, i)}</div>)}
            </div>
            <button type="button" className={`${arrowClass} left-2`} disabled={index === 0} onClick={(event) => { event.stopPropagation(); scrollToIndex(index - 1); }} aria-label="Previous image" title="Previous image"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" className={`${arrowClass} right-2`} disabled={index === slides.length - 1} onClick={(event) => { event.stopPropagation(); scrollToIndex(index + 1); }} aria-label="Next image" title="Next image"><ChevronRight className="h-4 w-4" /></button>
            <div className="absolute inset-x-0 bottom-1 flex justify-center">
              <div className="flex max-w-full overflow-x-auto rounded bg-[var(--card)]" role="group" aria-label="Choose image">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    className="flex h-8 w-8 shrink-0 items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--brand)]"
                    aria-label={`Go to image ${i + 1}`}
                    aria-pressed={i === index}
                    onClick={(event) => { event.stopPropagation(); scrollToIndex(i); }}
                  >
                    <span aria-hidden="true" className={`h-1.5 rounded-full ${i === index ? "w-4 bg-[var(--brand)]" : "w-1.5 bg-[var(--muted)]"}`} />
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : media(slides[0], 0)}
      </div>
      {viewerIndex !== null ? <ImageViewer images={images} initialIndex={viewerIndex} onClose={() => setViewerIndex(null)} /> : null}
    </>
  );
}
